<template>
  <v-container>
    <!-- Page Header -->

    <div class="page-header d-flex align-center mb-6">
      <div>
        <h1 class="text-h4 font-weight-light grey--text text--darken-3 mb-2">
          Tickets
          <v-chip 
            small 
            :color="ticketFilters.status ? getStatusColor(ticketFilters.status) : 'grey'" 
            text-color="white"
            class="ml-3"
          >
            {{ ticketFilters.status ? getStatusText(ticketFilters.status) : 'All' }}
          </v-chip>
        </h1>
        <p class="text-body-2 grey--text text--darken-1">
          Manage and track support requests
          <span v-if="ticketFilters.status" class="font-weight-medium">
            - Currently showing {{ getStatusText(ticketFilters.status).toLowerCase() }} tickets only
          </span>
        </p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        depressed
        color="grey darken-3"
        dark
        @click="$root.$emit('show-create-ticket')"
        class="text-none"
      >
        <v-icon left size="18">mdi-plus</v-icon>
        New Ticket
      </v-btn>
    </div>

    <!-- Filters -->
    <v-card elevation="0" color="white" outlined class="mb-6">
      <v-card-text class="pa-4">
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="ticketFilters.status"
              :items="statusOptions"
              label="Status"
              outlined
              dense
              clearable
              color="grey darken-3"
              background-color="grey lighten-5"
              class="filter-select"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="ticketFilters.priority"
              :items="priorityOptions"
              label="Priority"
              outlined
              dense
              clearable
              color="grey darken-3"
              background-color="grey lighten-5"
              class="filter-select"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="ticketFilters.category"
              :items="categories"
              item-text="name"
              item-value="id"
              label="Category"
              outlined
              dense
              clearable
              color="grey darken-3"
              background-color="grey lighten-5"
              class="filter-select"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="ticketFilters.search"
              label="Search..."
              outlined
              dense
              clearable
              color="grey darken-3"
              prepend-inner-icon="mdi-magnify"
              background-color="grey lighten-5"
              class="filter-select"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tickets Table -->
    <v-card elevation="0" color="white" outlined>
      <v-data-table
        :headers="ticketHeaders"
        :items="filteredTickets"
        :loading="loadingTickets"
        @click:row="openTicketDetail"
        class="tickets-table"
        hide-default-footer
        :items-per-page="15"
      >
        <template v-slot:item.id="{ item }">
          <span class="text-body-2 font-weight-medium grey--text text--darken-2">
            #{{ item.id }}
          </span>
        </template>

        <template v-slot:item.title="{ item }">
          <div>
            <div class="text-body-2 font-weight-medium grey--text text--darken-3">
              {{ item.title }}
            </div>
            <div class="text-caption grey--text text--darken-1">
              {{ truncateText(item.description, 80) }}
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            small
            :color="getStatusColor(item.status)"
            :text-color="getStatusTextColor(item.status)"
            class="font-weight-medium text-caption"
          >
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.priority="{ item }">
          <v-chip
            small
            outlined
            :color="getPriorityColor(item.priority)"
            class="font-weight-medium text-caption"
          >
            {{ getPriorityText(item.priority) }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          <span class="text-caption grey--text text--darken-1">
            {{ formatDateRelative(item.created_at) }}
          </span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click.stop="openTicketDetail(item)" color="grey darken-1">
            <v-icon size="18">mdi-eye-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <v-divider></v-divider>

      <div class="pa-4 d-flex align-center">
      <span class="text-caption grey--text text--darken-1">
      Showing {{ filteredTickets.length }} tickets
      <v-chip 
      v-if="ticketFilters.status"
      x-small 
      :color="getStatusColor(ticketFilters.status)" 
      text-color="white"
      class="ml-2"
      >
      {{ getStatusText(ticketFilters.status) }} only
      </v-chip>
      </span>
      <v-spacer></v-spacer>
        <v-pagination
          v-model="currentPage"
          :length="Math.ceil(filteredTickets.length / itemsPerPage)"
          total-visible="7"
          color="grey darken-3"
          class="custom-pagination"
        ></v-pagination>
      </div>
    </v-card>

    <!-- Ticket Detail Dialog -->
    <v-dialog v-model="showTicketDetail" max-width="900" scrollable>
      <v-card elevation="8" color="white" v-if="selectedTicket">
        <v-card-title class="pa-6 grey lighten-5">
          <div class="d-flex align-center w-100">
            <div>
              <div class="text-h6 font-weight-regular grey--text text--darken-3">
                {{ selectedTicket.title }}
              </div>
              <div class="mt-3">
                <v-chip
                  small
                  :color="getStatusColor(selectedTicket.status)"
                  :text-color="getStatusTextColor(selectedTicket.status)"
                  class="mr-2 font-weight-medium"
                >
                  {{ getStatusText(selectedTicket.status) }}
                </v-chip>
                <v-chip
                  small
                  outlined
                  :color="getPriorityColor(selectedTicket.priority)"
                  class="font-weight-medium"
                >
                  {{ getPriorityText(selectedTicket.priority) }}
                </v-chip>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click="showTicketDetail = false" color="grey darken-1">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <!-- ===== BOTONES DE ADMINISTRACIÓN DE TICKETS ===== -->
          <v-alert
            type="info"
            outlined
            class="mb-6"
            v-if="true"
          >
            <div class="d-flex align-center">
              <v-icon left color="blue">mdi-cog</v-icon>
              <span class="text-subtitle-2 font-weight-bold">Ticket Management Actions</span>
            </div>
            
            <div class="d-flex align-center flex-wrap mt-3">
              <!-- Close Ticket Button -->
              <v-btn
                v-if="selectedTicket.status === 'open'"
                depressed
                color="green"
                dark
                small
                @click="closeTicket"
                :loading="loading"
                class="text-none mr-2 mb-2"
              >
                <v-icon left size="16">mdi-check</v-icon>
                Close Ticket
              </v-btn>
              
              <!-- Reopen Ticket Button -->
              <v-btn
                v-if="selectedTicket.status === 'closed'"
                depressed
                color="blue"
                dark
                small
                @click="reopenTicket"
                :loading="loading"
                class="text-none mr-2 mb-2"
              >
                <v-icon left size="16">mdi-refresh</v-icon>
                Reopen Ticket
              </v-btn>

              <!-- Edit Ticket Button -->
              <v-btn
                depressed
                color="purple"
                dark
                small
                @click="editTicket"
                class="text-none mr-2 mb-2"
              >
                <v-icon left size="16">mdi-pencil</v-icon>
                Edit Ticket
              </v-btn>

              <!-- Change Priority Button -->
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    depressed
                    color="orange"
                    dark
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="text-none mr-2 mb-2"
                  >
                    <v-icon left size="16">mdi-flag</v-icon>
                    Priority
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="priority in priorityOptions"
                    :key="priority.value"
                    @click="changePriority(priority.value)"
                  >
                    <v-list-item-title>{{ priority.text }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Assign Agent Button -->
              <v-btn
                depressed
                color="indigo"
                dark
                small
                @click="assignAgent"
                class="text-none mb-2"
              >
                <v-icon left size="16">mdi-account-plus</v-icon>
                Assign Agent
              </v-btn>
            </div>
          </v-alert>
          <!-- ===== FIN BOTONES DE ADMINISTRACIÓN ===== -->

          <div class="mb-6">
            <div class="text-subtitle-2 grey--text text--darken-2 mb-3">Description</div>
            <p class="text-body-2 grey--text text--darken-3">
              {{ selectedTicket.description }}
            </p>
          </div>

          <div class="text-caption grey--text text--darken-1 mb-6">
            Created {{ formatDate(selectedTicket.created_at) }}
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- Comments Section -->
          <div class="comments-section">
            <div class="text-h6 font-weight-regular grey--text text--darken-3 mb-6">
              Comments
            </div>

            <div v-if="loadingComments" class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="grey darken-3"
                size="32"
              ></v-progress-circular>
            </div>

            <div v-else-if="comments.length === 0" class="text-center py-8">
              <v-icon size="48" color="grey lighten-2">mdi-comment-off-outline</v-icon>
              <p class="text-body-2 grey--text text--darken-1 mt-4">No comments yet</p>
            </div>

            <div v-else>
              <div
                v-for="comment in comments"
                :key="comment.id"
                class="comment-item mb-4"
              >
                <v-card elevation="0" outlined class="pa-4">
                  <div class="d-flex align-start">
                    <v-avatar size="36" color="grey lighten-2" class="mr-3">
                      <span class="text-caption grey--text text--darken-2">
                        {{ getUserInitials(comment.user_name) }}
                      </span>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="d-flex align-center mb-2">
                        <span
                          class="text-subtitle-2 font-weight-medium grey--text text--darken-3"
                        >
                          {{ comment.user_name }}
                        </span>
                        <span class="text-caption grey--text text--darken-1 ml-2">
                          {{ formatDateRelative(comment.created_at) }}
                        </span>
                      </div>
                      <div class="text-body-2 grey--text text--darken-2">
                        {{ comment.content }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </div>
            </div>

            <!-- Add Comment -->
            <v-divider class="my-6"></v-divider>
            <v-textarea
              v-model="newComment"
              label="Add a comment..."
              outlined
              rows="3"
              color="grey darken-3"
              background-color="grey lighten-5"
              hide-details
              class="mb-4"
            ></v-textarea>
            <v-btn
              depressed
              color="grey darken-3"
              dark
              @click="addComment"
              :loading="addingComment"
              :disabled="!newComment.trim() || selectedTicket.status === 'closed'"
              class="text-none"
            >
              Add Comment
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "TicketsView",
  props: {
    apiUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // Tickets Data
      tickets: [],
      loadingTickets: false,
      selectedTicket: null,
      comments: [],
      loadingComments: false,
      newComment: "",
      addingComment: false,
      showTicketDetail: false,
      currentPage: 1,
      itemsPerPage: 15,

      // Loading states for actions
      loading: false,

      // Filters
      ticketFilters: {
        status: "open", // Por defecto mostrar solo tickets abiertos
        priority: null,
        category: null,
        search: "",
      },

      statusOptions: [
        { text: "Open", value: "open" },
        { text: "Closed", value: "closed" },
        { text: "All Tickets", value: null }, // Opción para ver todos
      ],

      priorityOptions: [
        { text: "Low", value: "low" },
        { text: "Medium", value: "medium" },
        { text: "High", value: "high" },
      ],

      // Table Headers
      ticketHeaders: [
        { text: "ID", value: "id", width: "80px", sortable: false },
        { text: "Subject", value: "title", width: "40%" },
        { text: "Status", value: "status", width: "120px" },
        { text: "Priority", value: "priority", width: "120px" },
        { text: "Created", value: "created_at", width: "140px" },
        { text: "", value: "actions", sortable: false, width: "60px" },
      ],

      categories: [],
    };
  },

  computed: {
    filteredTickets() {
      let filtered = [...this.tickets];

      if (this.ticketFilters.status) {
        filtered = filtered.filter((t) => t.status === this.ticketFilters.status);
      }

      if (this.ticketFilters.priority) {
        filtered = filtered.filter((t) => t.priority === this.ticketFilters.priority);
      }

      if (this.ticketFilters.category) {
        filtered = filtered.filter((t) => t.category_id === this.ticketFilters.category);
      }

      if (this.ticketFilters.search) {
        const search = this.ticketFilters.search.toLowerCase();
        filtered = filtered.filter(
          (t) =>
            t.title.toLowerCase().includes(search) ||
            t.description.toLowerCase().includes(search)
        );
      }

      return filtered;
    },

    authToken() {
      return localStorage.getItem("support_token");
    },
  },

  mounted() {
    this.fetchTickets();
    this.fetchCategories();

    // Listen for ticket creation events
    this.$root.$on("ticket-created", this.fetchTickets);
    this.$root.$on("show-create-ticket", () => {
      this.$root.$emit("show-create-ticket-dialog");
    });
  },

  beforeDestroy() {
    this.$root.$off("ticket-created", this.fetchTickets);
    this.$root.$off("show-create-ticket");
  },

  methods: {
    async fetchTickets() {
      if (!this.authToken) return;

      this.loadingTickets = true;
      try {
        this.tickets = await this.makeApiCall(`${this.apiUrl}/tickets`);
      } catch (err) {
        console.error("Error loading tickets:", err);
      } finally {
        this.loadingTickets = false;
      }
    },

    async fetchCategories() {
      try {
        const response = await this.makeApiCall(`${this.apiUrl}/categories`);
        this.categories = response.filter((cat) => cat.is_active !== false);
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    },

    openTicketDetail(ticket) {
      this.selectedTicket = ticket;
      this.showTicketDetail = true;
      this.loadTicketComments(ticket.id);
    },

    async loadTicketComments(ticketId) {
      this.loadingComments = true;
      try {
        this.comments = await this.makeApiCall(
          `${this.apiUrl}/tickets/${ticketId}/updates`
        );
      } catch (err) {
        this.comments = [];
      } finally {
        this.loadingComments = false;
      }
    },

    async addComment() {
      if (!this.newComment.trim() || !this.selectedTicket) return;

      this.addingComment = true;
      try {
        const formData = new FormData();
        formData.append("content", this.newComment);
        formData.append("type", "comment");
        formData.append("is_public", "true");

        const response = await fetch(
          `${this.apiUrl}/tickets/${this.selectedTicket.id}/updates`,
          {
            method: "POST",
            headers: { Authorization: this.authToken },
            body: formData,
          }
        );

        if (response.ok) {
          this.newComment = "";
          await this.loadTicketComments(this.selectedTicket.id);
        }
      } catch (err) {
        console.error("Failed to add comment:", err);
      } finally {
        this.addingComment = false;
      }
    },

    // ===== NUEVAS FUNCIONES DE ADMINISTRACIÓN =====
    async closeTicket() {
      this.loading = true;
      try {
        console.log('Cerrando ticket:', this.selectedTicket.id);
        
        // Llamada al API para cerrar ticket
        const response = await fetch(
          `${this.apiUrl}/tickets/${this.selectedTicket.id}/close`,
          {
            method: "POST",
            headers: { 
              Authorization: this.authToken,
              'Content-Type': 'application/json'
            },
          }
        );

        if (response.ok) {
          this.selectedTicket.status = 'closed';
          this.selectedTicket.closed_at = new Date().toISOString();
          
          // Refresh tickets list
          await this.fetchTickets();
          
          this.$root.$emit('show-snackbar', {
            message: 'Ticket closed successfully!',
            color: 'success'
          });
        } else {
          throw new Error('Failed to close ticket');
        }
      } catch (err) {
        console.error("Error closing ticket:", err);
        this.$root.$emit('show-snackbar', {
          message: 'Error closing ticket: ' + err.message,
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async reopenTicket() {
      this.loading = true;
      try {
        console.log('Reabriendo ticket:', this.selectedTicket.id);
        
        // Llamada al API para reabrir ticket
        const response = await fetch(
          `${this.apiUrl}/tickets/${this.selectedTicket.id}`,
          {
            method: "PUT",
            headers: { 
              Authorization: this.authToken,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status: 'open' })
          }
        );

        if (response.ok) {
          this.selectedTicket.status = 'open';
          this.selectedTicket.closed_at = null;
          
          // Refresh tickets list
          await this.fetchTickets();
          
          this.$root.$emit('show-snackbar', {
            message: 'Ticket reopened successfully!',
            color: 'success'
          });
        } else {
          throw new Error('Failed to reopen ticket');
        }
      } catch (err) {
        console.error("Error reopening ticket:", err);
        this.$root.$emit('show-snackbar', {
          message: 'Error reopening ticket: ' + err.message,
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    editTicket() {
      console.log('Edit ticket:', this.selectedTicket.id);
      // Aquí abrirías un diálogo de edición
      this.$root.$emit('show-snackbar', {
        message: 'Edit ticket functionality - Coming soon!',
        color: 'info'
      });
    },

    async changePriority(newPriority) {
      this.loading = true;
      try {
        console.log('Cambiando prioridad a:', newPriority);
        
        const response = await fetch(
          `${this.apiUrl}/tickets/${this.selectedTicket.id}`,
          {
            method: "PUT",
            headers: { 
              Authorization: this.authToken,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ priority: newPriority })
          }
        );

        if (response.ok) {
          this.selectedTicket.priority = newPriority;
          
          // Refresh tickets list
          await this.fetchTickets();
          
          this.$root.$emit('show-snackbar', {
            message: `Priority changed to ${newPriority}!`,
            color: 'success'
          });
        } else {
          throw new Error('Failed to change priority');
        }
      } catch (err) {
        console.error("Error changing priority:", err);
        this.$root.$emit('show-snackbar', {
          message: 'Error changing priority: ' + err.message,
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    assignAgent() {
      console.log('Assign agent to ticket:', this.selectedTicket.id);
      // Aquí abrirías un diálogo para seleccionar agente
      this.$root.$emit('show-snackbar', {
        message: 'Agent assignment functionality - Coming soon!',
        color: 'info'
      });
    },

    // API Helper
    async makeApiCall(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
          Authorization: this.authToken,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Error ${response.status}`);
      }

      return await response.json();
    },

    // Utility Methods
    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.slice(0, length) + "..." : text;
    },

    getUserInitials(name) {
      if (!name) return "U";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    getStatusText(status) {
      const texts = {
        open: "Open",
        closed: "Closed",
      };
      return texts[status] || status;
    },

    getStatusColor(status) {
      const colors = {
        open: "blue",
        closed: "grey",
      };
      return colors[status] || "grey";
    },

    getStatusTextColor(status) {
      const colors = {
        open: "white",
        closed: "white",
      };
      return colors[status] || "white";
    },

    getPriorityText(priority) {
      const texts = {
        low: "Low",
        medium: "Medium",
        high: "High",
      };
      return texts[priority] || priority;
    },

    getPriorityColor(priority) {
      const colors = {
        low: "green",
        medium: "blue",
        high: "orange",
      };
      return colors[priority] || "grey";
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("en-US");
    },

    formatDateRelative(date) {
      if (!date) return "";
      const now = new Date();
      const past = new Date(date);
      const diffMs = now - past;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return past.toLocaleDateString("en-US");
    },
  },
};
</script>

<style scoped>
/* Tickets Styles */
.tickets-view {
  min-height: 100vh;
  background: #fafafa;
}

.filter-select >>> .v-input__slot {
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.tickets-table {
  border-radius: 12px !important;
}

.tickets-table >>> .v-data-table__wrapper {
  border-radius: 12px !important;
}

.tickets-table >>> tbody tr {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.tickets-table >>> tbody tr:hover {
  background: rgba(0, 0, 0, 0.02) !important;
}

.tickets-table >>> .v-data-table-header {
  background: #f8f9fa !important;
}

.custom-pagination >>> .v-pagination__item {
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  margin: 0 2px !important;
}

.custom-pagination >>> .v-pagination__item--active {
  background: rgba(0, 0, 0, 0.87) !important;
  color: white !important;
}

.comment-item >>> .v-card {
  border-radius: 8px !important;
  background: #f8f9fa !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

/* Form Styles */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.v-select--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
}

.v-textarea >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
}

/* Button Styles */
.v-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  letter-spacing: 0 !important;
}

/* Chip Styles */
.v-chip {
  border-radius: 6px !important;
  font-weight: 500 !important;
  font-size: 12px !important;
}

.v-chip.v-chip--outlined {
  border-width: 1px !important;
}

/* Card Styles */
.v-card {
  border-radius: 12px !important;
}

.v-card.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

/* Dialog Styles */
.v-dialog >>> .v-card {
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

/* Responsive Design */
@media (max-width: 600px) {
  .page-header {
    margin-bottom: 24px;
  }
}
</style>
